import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../App';
import { toast } from 'sonner';
import { formatDate } from '../utils/dateFormat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { 
  Settings as SettingsIcon,
  Database,
  Clock,
  RefreshCw,
  Save,
  TestTube,
  Play,
  CheckCircle,
  XCircle,
  AlertCircle,
  Server,
  Key,
  Calendar,
  Globe,
  FileCode,
  Users,
  Wrench,
  Plus,
  Edit,
  Trash2,
  Shield,
  Brain,
  Sparkles,
  FileSpreadsheet,
  ClipboardList,
  Eye,
  X,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const DAYS_OF_WEEK = [
  { value: 'sunday', label: 'Sunday' },
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' }
];

const TIMEZONES = [
  { value: 'UTC', label: 'UTC' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney (AEST/AEDT)' },
  { value: 'Australia/Melbourne', label: 'Australia/Melbourne (AEST/AEDT)' },
  { value: 'Australia/Brisbane', label: 'Australia/Brisbane (AEST)' },
  { value: 'Australia/Perth', label: 'Australia/Perth (AWST)' },
  { value: 'Australia/Adelaide', label: 'Australia/Adelaide (ACST/ACDT)' },
  { value: 'Pacific/Auckland', label: 'New Zealand (NZST/NZDT)' },
  { value: 'America/New_York', label: 'US Eastern' },
  { value: 'America/Los_Angeles', label: 'US Pacific' },
  { value: 'Europe/London', label: 'UK (GMT/BST)' }
];

const EQUIPMENT_CATEGORIES = [
  'Crane',
  'Forklift',
  'Elevated Work Platform',
  'Excavator',
  'Truck',
  'Other'
];

const SettingsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  // Settings state
  const [settings, setSettings] = useState({
    sql_server: '',
    sql_database: '',
    sql_auth_type: 'sql',
    sql_username: '',
    sql_password: '',
    sql_port: 1433,
    sql_encrypt: true,
    sql_trust_cert: false,
    timesheet_stored_proc: '',
    docket_stored_proc: '',
    sync_enabled: false,
    sync_day: 'sunday',
    sync_time: '22:00',
    sync_timezone: 'UTC',
    last_sync_at: null,
    last_sync_status: null,
    last_sync_message: null,
    ai_suggestions_enabled: false,
    ai_min_patterns_for_suggestion: 5
  });
  
  // AI Learning state
  const [aiStats, setAiStats] = useState(null);
  const [loadingAiStats, setLoadingAiStats] = useState(false);

  // Roles & Equipment state
  const [roles, setRoles] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [loadingEquipment, setLoadingEquipment] = useState(false);
  
  // Dialog state
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [equipmentDialogOpen, setEquipmentDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [roleForm, setRoleForm] = useState({ name: '', description: '' });
  const [equipmentForm, setEquipmentForm] = useState({ name: '', description: '', category: '' });
  
  // Scheduler status state
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  
  // Public holidays state
  const [holidays, setHolidays] = useState([]);
  const [holidayDialogOpen, setHolidayDialogOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [holidayForm, setHolidayForm] = useState({ date: '', name: '', state: '' });
  
  // Employee types state
  const [employeeTypes, setEmployeeTypes] = useState([]);
  const [employeeTypeDialogOpen, setEmployeeTypeDialogOpen] = useState(false);
  const [editingEmployeeType, setEditingEmployeeType] = useState(null);
  const [employeeTypeForm, setEmployeeTypeForm] = useState({ 
    name: '', 
    description: '', 
    receives_all_allowances: true 
  });

  // Week data fetch state
  const [weekCommencing, setWeekCommencing] = useState('');
  const [fetchingWeekData, setFetchingWeekData] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [confirmingImport, setConfirmingImport] = useState(false);
  const [importSuccessData, setImportSuccessData] = useState(null);
  const [importSuccessModalOpen, setImportSuccessModalOpen] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchRoles();
    fetchEquipmentTypes();
    fetchSchedulerStatus();
    fetchHolidays();
    fetchEmployeeTypes();
    fetchAiStats();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await API.get('/settings');
      setSettings(prev => ({
        ...prev,
        ...response.data,
        sql_password: ''
      }));
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchAiStats = async () => {
    setLoadingAiStats(true);
    try {
      const response = await API.get('/ai/learning-stats');
      setAiStats(response.data);
    } catch (error) {
      console.error('Failed to load AI stats');
    } finally {
      setLoadingAiStats(false);
    }
  };

  const handleAiSettingsChange = async (field, value) => {
    const newSettings = { ...settings, [field]: value };
    setSettings(newSettings);
    
    try {
      await API.put('/ai/settings', { [field]: value });
      toast.success('AI settings updated');
      fetchAiStats();
    } catch (error) {
      toast.error('Failed to update AI settings');
      // Revert
      setSettings(settings);
    }
  };

  const fetchSchedulerStatus = async () => {
    try {
      const response = await API.get('/settings/scheduler-status');
      setSchedulerStatus(response.data);
    } catch (error) {
      console.error('Failed to load scheduler status');
    }
  };

  const fetchRoles = async () => {
    setLoadingRoles(true);
    try {
      const response = await API.get('/roles');
      setRoles(response.data);
    } catch (error) {
      toast.error('Failed to load roles');
    } finally {
      setLoadingRoles(false);
    }
  };

  const fetchEquipmentTypes = async () => {
    setLoadingEquipment(true);
    try {
      const response = await API.get('/equipment-types');
      setEquipmentTypes(response.data);
    } catch (error) {
      toast.error('Failed to load equipment types');
    } finally {
      setLoadingEquipment(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const dataToSend = { ...settings };
      if (!dataToSend.sql_password) {
        delete dataToSend.sql_password;
      }
      await API.put('/settings', dataToSend);
      toast.success('Settings saved successfully');
      // Refresh scheduler status after saving
      await fetchSchedulerStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await API.post('/settings/test-connection');
      setTestResult({ success: response.data.success, message: response.data.message });
      if (response.data.success) toast.success('Connection test successful');
    } catch (error) {
      setTestResult({ success: false, message: error.response?.data?.detail || 'Connection test failed' });
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const response = await API.post('/settings/sync-now');
      toast.success(response.data.message);
      await fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleFetchWeekData = async () => {
    if (!weekCommencing) {
      toast.error('Please select a Week Commencing date');
      return;
    }
    
    setFetchingWeekData(true);
    try {
      const response = await API.post('/settings/fetch-week-data', { 
        week_commencing: weekCommencing 
      });
      
      if (response.data.preview) {
        // Store preview data and open modal
        setPreviewData(response.data);
        setPreviewModalOpen(true);
      } else if (response.data.batch_id) {
        // Legacy response - batch was created directly
        toast.success(
          `${response.data.message}\n` +
          `Timesheets: ${response.data.timesheet_count}, Dockets: ${response.data.docket_count}\n` +
          `Matched: ${response.data.matched_count}, Discrepancies: ${response.data.discrepancy_count}`,
          { duration: 5000 }
        );
      } else {
        toast.info(response.data.message || 'No data found for the selected week');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch week data');
    } finally {
      setFetchingWeekData(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!previewData) return;
    
    setConfirmingImport(true);
    try {
      const response = await API.post('/settings/confirm-week-import', {
        week_commencing: previewData.week_commencing,
        batch_name: previewData.batch_name,
        timesheet_entries: previewData.timesheet_entries,
        docket_entries: previewData.docket_entries,
        docket_items: previewData.docket_items || []
      });
      
      // Store success data and show success modal
      setImportSuccessData({
        batch_name: response.data.batch_name || previewData.batch_name,
        batch_id: response.data.batch_id,
        timesheet_count: response.data.timesheet_count || 0,
        docket_count: response.data.docket_count || 0,
        docket_items_count: response.data.docket_items_count || 0,
        validation_count: response.data.validation_count || 0,
        matched_count: response.data.matched_count || 0,
        discrepancy_count: response.data.discrepancy_count || 0
      });
      
      // Close preview modal and show success modal
      setPreviewModalOpen(false);
      setPreviewData(null);
      setWeekCommencing('');
      setImportSuccessModalOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm import');
    } finally {
      setConfirmingImport(false);
    }
  };

  const handleCancelPreview = () => {
    setPreviewModalOpen(false);
    setPreviewData(null);
    toast.info('Import cancelled');
  };

  const handleCloseSuccessModal = () => {
    setImportSuccessModalOpen(false);
    setImportSuccessData(null);
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  // Role handlers
  const openRoleDialog = (role = null) => {
    if (role) {
      setEditingRole(role);
      setRoleForm({ name: role.name, description: role.description || '' });
    } else {
      setEditingRole(null);
      setRoleForm({ name: '', description: '' });
    }
    setRoleDialogOpen(true);
  };

  const handleSaveRole = async () => {
    if (!roleForm.name.trim()) {
      toast.error('Role name is required');
      return;
    }
    try {
      if (editingRole) {
        await API.put(`/roles/${editingRole.id}`, roleForm);
        toast.success('Role updated');
      } else {
        await API.post('/roles', roleForm);
        toast.success('Role created');
      }
      setRoleDialogOpen(false);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save role');
    }
  };

  const handleDeleteRole = async (roleId) => {
    try {
      await API.delete(`/roles/${roleId}`);
      toast.success('Role deleted');
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete role');
    }
  };

  // Equipment handlers
  const openEquipmentDialog = (equipment = null) => {
    if (equipment) {
      setEditingEquipment(equipment);
      setEquipmentForm({ 
        name: equipment.name, 
        description: equipment.description || '',
        category: equipment.category || ''
      });
    } else {
      setEditingEquipment(null);
      setEquipmentForm({ name: '', description: '', category: '' });
    }
    setEquipmentDialogOpen(true);
  };

  const handleSaveEquipment = async () => {
    if (!equipmentForm.name.trim()) {
      toast.error('Equipment name is required');
      return;
    }
    try {
      if (editingEquipment) {
        await API.put(`/equipment-types/${editingEquipment.id}`, equipmentForm);
        toast.success('Equipment type updated');
      } else {
        await API.post('/equipment-types', equipmentForm);
        toast.success('Equipment type created');
      }
      setEquipmentDialogOpen(false);
      fetchEquipmentTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save equipment type');
    }
  };

  const handleDeleteEquipment = async (equipId) => {
    try {
      await API.delete(`/equipment-types/${equipId}`);
      toast.success('Equipment type deleted');
      fetchEquipmentTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete equipment type');
    }
  };

  // Public holidays functions
  const fetchHolidays = async () => {
    try {
      const response = await API.get('/public-holidays');
      setHolidays(response.data);
    } catch (error) {
      console.error('Failed to load public holidays');
    }
  };

  const handleSaveHoliday = async () => {
    if (!holidayForm.date || !holidayForm.name) {
      toast.error('Date and name are required');
      return;
    }
    try {
      if (editingHoliday) {
        await API.put(`/public-holidays/${editingHoliday.id}`, holidayForm);
        toast.success('Holiday updated');
      } else {
        await API.post('/public-holidays', holidayForm);
        toast.success('Holiday created');
      }
      setHolidayDialogOpen(false);
      setEditingHoliday(null);
      setHolidayForm({ date: '', name: '', state: '' });
      fetchHolidays();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save holiday');
    }
  };

  const handleDeleteHoliday = async (holidayId) => {
    try {
      await API.delete(`/public-holidays/${holidayId}`);
      toast.success('Holiday deleted');
      fetchHolidays();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete holiday');
    }
  };

  // Employee Types functions
  const fetchEmployeeTypes = async () => {
    try {
      const response = await API.get('/employee-types');
      setEmployeeTypes(response.data);
    } catch (error) {
      console.error('Failed to load employee types');
    }
  };

  const handleSaveEmployeeType = async () => {
    if (!employeeTypeForm.name) {
      toast.error('Name is required');
      return;
    }
    try {
      if (editingEmployeeType) {
        await API.put(`/employee-types/${editingEmployeeType.id}`, employeeTypeForm);
        toast.success('Employee type updated');
      } else {
        await API.post('/employee-types', employeeTypeForm);
        toast.success('Employee type created');
      }
      setEmployeeTypeDialogOpen(false);
      setEditingEmployeeType(null);
      setEmployeeTypeForm({ name: '', description: '', receives_all_allowances: true });
      fetchEmployeeTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save employee type');
    }
  };

  const handleDeleteEmployeeType = async (typeId) => {
    try {
      await API.delete(`/employee-types/${typeId}`);
      toast.success('Employee type deleted');
      fetchEmployeeTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete employee type');
    }
  };

  const getSyncStatusBadge = () => {
    if (!settings.last_sync_status) return null;
    switch (settings.last_sync_status) {
      case 'success':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Success</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
      case 'running':
        return <Badge className="bg-blue-100 text-blue-700"><RefreshCw className="w-3 h-3 mr-1 animate-spin" />Running</Badge>;
      default:
        return <Badge variant="outline">{settings.last_sync_status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-slate-600 to-slate-700">
              <SettingsIcon className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
          </div>
          <p className="text-slate-600 mt-1 ml-12">Configure system settings, roles, and equipment types</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="gap-2">
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Settings
        </Button>
      </div>

      <Tabs defaultValue="roles" className="space-y-6">
        <TabsList>
          <TabsTrigger value="roles" className="gap-2">
            <Users className="w-4 h-4" />
            Roles
          </TabsTrigger>
          <TabsTrigger value="equipment" className="gap-2">
            <Wrench className="w-4 h-4" />
            Equipment Types
          </TabsTrigger>
          <TabsTrigger value="employee-types" className="gap-2">
            <Users className="w-4 h-4" />
            Employee Types
          </TabsTrigger>
          <TabsTrigger value="holidays" className="gap-2">
            <Calendar className="w-4 h-4" />
            Public Holidays
          </TabsTrigger>
          <TabsTrigger value="ai" className="gap-2">
            <Brain className="w-4 h-4" />
            AI Learning
          </TabsTrigger>
          <TabsTrigger value="sql" className="gap-2">
            <Database className="w-4 h-4" />
            SQL Connection
          </TabsTrigger>
          <TabsTrigger value="procedures" className="gap-2">
            <FileCode className="w-4 h-4" />
            Stored Procedures
          </TabsTrigger>
          <TabsTrigger value="schedule" className="gap-2">
            <Clock className="w-4 h-4" />
            Sync Schedule
          </TabsTrigger>
        </TabsList>

        {/* Roles Tab */}
        <TabsContent value="roles">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Roles
                  </CardTitle>
                  <CardDescription>
                    Define roles that can be assigned to docket entries and rules
                  </CardDescription>
                </div>
                <Button onClick={() => openRoleDialog()} className="gap-2">
                  <Plus className="w-4 h-4" />
                  Add Role
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingRoles ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
                </div>
              ) : roles.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No roles defined yet
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Role Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {roles.map((role) => (
                      <TableRow key={role.id}>
                        <TableCell className="font-medium">{role.name}</TableCell>
                        <TableCell className="text-slate-500">{role.description || '—'}</TableCell>
                        <TableCell>
                          {role.is_system ? (
                            <Badge variant="outline" className="gap-1">
                              <Shield className="w-3 h-3" />
                              System
                            </Badge>
                          ) : (
                            <Badge variant="secondary">Custom</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => openRoleDialog(role)}
                            >
                              <Edit className="w-3 h-3" />
                            </Button>
                            {!role.is_system && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="outline" size="sm" className="text-red-600">
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Role?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will delete the role &quot;{role.name}&quot;. This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleDeleteRole(role.id)} className="bg-red-600">
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Equipment Types Tab */}
        <TabsContent value="equipment">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Wrench className="w-5 h-5" />
                    Equipment Types
                  </CardTitle>
                  <CardDescription>
                    Define equipment types that can be assigned to docket entries and rules
                  </CardDescription>
                </div>
                <Button onClick={() => openEquipmentDialog()} className="gap-2">
                  <Plus className="w-4 h-4" />
                  Add Equipment Type
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingEquipment ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
                </div>
              ) : equipmentTypes.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No equipment types defined yet
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Equipment Name</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {equipmentTypes.map((equip) => (
                      <TableRow key={equip.id}>
                        <TableCell className="font-medium">{equip.name}</TableCell>
                        <TableCell>
                          {equip.category ? (
                            <Badge variant="outline">{equip.category}</Badge>
                          ) : '—'}
                        </TableCell>
                        <TableCell className="text-slate-500">{equip.description || '—'}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => openEquipmentDialog(equip)}
                            >
                              <Edit className="w-3 h-3" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="outline" size="sm" className="text-red-600">
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Equipment Type?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will delete &quot;{equip.name}&quot;. This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => handleDeleteEquipment(equip.id)} className="bg-red-600">
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Employee Types Tab */}
        <TabsContent value="employee-types">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Employee Types
                  </CardTitle>
                  <CardDescription>
                    Manage employee type classifications for rule assignments
                  </CardDescription>
                </div>
                <Button onClick={() => {
                  setEditingEmployeeType(null);
                  setEmployeeTypeForm({ name: '', description: '', receives_all_allowances: true });
                  setEmployeeTypeDialogOpen(true);
                }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Type
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>How it works:</strong> When processing payroll, rules with specific Employee Types only apply to employees of that type.
                  Types marked as &quot;Full Allowances&quot; receive all rule-based allowances; others receive only base allowances.
                </p>
              </div>
              {employeeTypes.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No employee types defined</p>
                  <p className="text-sm">Click &quot;Add Type&quot; to create employee classifications</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Allowances</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {employeeTypes.map((type) => (
                      <TableRow key={type.id}>
                        <TableCell className="font-medium">{type.name}</TableCell>
                        <TableCell className="text-sm text-slate-500">{type.description || '-'}</TableCell>
                        <TableCell>
                          <Badge className={type.receives_all_allowances ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}>
                            {type.receives_all_allowances ? 'Full Allowances' : 'Base Only'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditingEmployeeType(type);
                                setEmployeeTypeForm({
                                  name: type.name,
                                  description: type.description || '',
                                  receives_all_allowances: type.receives_all_allowances
                                });
                                setEmployeeTypeDialogOpen(true);
                              }}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:text-red-600"
                              onClick={() => handleDeleteEmployeeType(type.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Public Holidays Tab */}
        <TabsContent value="holidays">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Public Holidays
                  </CardTitle>
                  <CardDescription>
                    Manage public holidays that trigger overtime rates
                  </CardDescription>
                </div>
                <Button onClick={() => {
                  setEditingHoliday(null);
                  setHolidayForm({ date: '', name: '', state: '' });
                  setHolidayDialogOpen(true);
                }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Holiday
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {holidays.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No public holidays defined</p>
                  <p className="text-sm">Add holidays to ensure overtime is calculated correctly</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Holiday Name</TableHead>
                      <TableHead>State</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {holidays.map((holiday) => (
                      <TableRow key={holiday.id}>
                        <TableCell className="font-medium">{formatDate(holiday.date)}</TableCell>
                        <TableCell>{holiday.name}</TableCell>
                        <TableCell>
                          {holiday.state ? (
                            <Badge variant="outline">{holiday.state}</Badge>
                          ) : (
                            <span className="text-slate-400">All</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditingHoliday(holiday);
                                setHolidayForm({
                                  date: holiday.date,
                                  name: holiday.name,
                                  state: holiday.state || ''
                                });
                                setHolidayDialogOpen(true);
                              }}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:text-red-600"
                              onClick={() => handleDeleteHoliday(holiday.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Learning Tab */}
        <TabsContent value="ai">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="w-5 h-5" />
                AI Resolution Learning
              </CardTitle>
              <CardDescription>
                AI learns from how you resolve timesheet vs docket discrepancies and suggests resolutions
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-1">
                  <Label className="text-base font-medium">Enable AI Suggestions</Label>
                  <p className="text-sm text-slate-500">
                    When enabled, AI will suggest how to resolve discrepancies based on your history
                  </p>
                </div>
                <Switch
                  checked={settings.ai_suggestions_enabled}
                  onCheckedChange={(checked) => handleAiSettingsChange('ai_suggestions_enabled', checked)}
                />
              </div>
              
              {/* Minimum Patterns */}
              <div className="space-y-2">
                <Label>Minimum Patterns Before Suggesting</Label>
                <p className="text-sm text-slate-500 mb-2">
                  AI will wait until it has learned from this many resolutions before making suggestions
                </p>
                <Select 
                  value={String(settings.ai_min_patterns_for_suggestion || 5)}
                  onValueChange={(val) => handleAiSettingsChange('ai_min_patterns_for_suggestion', parseInt(val))}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3</SelectItem>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Learning Stats */}
              <div className="border rounded-lg p-4 bg-slate-50">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-purple-500" />
                    Learning Progress
                  </h4>
                  <Button variant="ghost" size="sm" onClick={fetchAiStats} disabled={loadingAiStats}>
                    <RefreshCw className={`w-4 h-4 ${loadingAiStats ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
                
                {aiStats ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-3 bg-white rounded-lg">
                        <p className="text-2xl font-bold text-purple-600">{aiStats.total_patterns}</p>
                        <p className="text-xs text-slate-500">Patterns Learned</p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg">
                        <p className="text-2xl font-bold text-slate-700">{aiStats.min_patterns_required}</p>
                        <p className="text-xs text-slate-500">Min Required</p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg">
                        <Badge className={aiStats.ready_for_suggestions ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                          {aiStats.ready_for_suggestions ? 'Ready' : 'Learning'}
                        </Badge>
                        <p className="text-xs text-slate-500 mt-1">Status</p>
                      </div>
                    </div>
                    
                    {aiStats.source_breakdown && Object.keys(aiStats.source_breakdown).length > 0 && (
                      <div>
                        <Label className="text-xs text-slate-500">Resolution Preferences</Label>
                        <div className="flex gap-2 mt-1">
                          {Object.entries(aiStats.source_breakdown).map(([source, count]) => (
                            <Badge key={source} variant="outline" className="capitalize">
                              {source}: {count}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {aiStats.top_employees?.length > 0 && (
                      <div>
                        <Label className="text-xs text-slate-500">Most Learned Employees</Label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {aiStats.top_employees.map((emp) => (
                            <Badge key={emp.id} variant="outline" className="text-xs">
                              {emp.name || emp.id}: {emp.patterns}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <p className="text-sm text-slate-600">{aiStats.message}</p>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">Loading AI stats...</p>
                )}
              </div>
              
              {/* Info Box */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-800 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  How AI Learning Works
                </h4>
                <ul className="mt-2 text-sm text-blue-700 list-disc list-inside space-y-1">
                  <li>AI learns every time you resolve a timesheet vs docket discrepancy</li>
                  <li>It tracks which source you prefer for different employees, sites, and discrepancy types</li>
                  <li>Suggestions are based on pattern matching, not external AI services</li>
                  <li>Learning continues in the background even when suggestions are disabled</li>
                  <li>All learning is isolated to your organization (tenant)</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SQL Connection Tab */}
        <TabsContent value="sql">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Server className="w-5 h-5" />
                SQL Server Connection
              </CardTitle>
              <CardDescription>
                Configure the connection to your SQL Server database
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Server Address</Label>
                  <Input
                    placeholder="server.database.windows.net"
                    value={settings.sql_server || ''}
                    onChange={(e) => updateSetting('sql_server', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Port</Label>
                  <Input
                    type="number"
                    placeholder="1433"
                    value={settings.sql_port || 1433}
                    onChange={(e) => updateSetting('sql_port', parseInt(e.target.value) || 1433)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Database Name</Label>
                <Input
                  placeholder="PayrollDB"
                  value={settings.sql_database || ''}
                  onChange={(e) => updateSetting('sql_database', e.target.value)}
                />
              </div>

              <div className="border-t pt-6">
                <Label className="text-sm font-semibold mb-4 block">Authentication</Label>
                <div className="space-y-4">
                  <Select 
                    value={settings.sql_auth_type} 
                    onValueChange={(value) => updateSetting('sql_auth_type', value)}
                  >
                    <SelectTrigger className="w-64">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sql">SQL Server Authentication</SelectItem>
                      <SelectItem value="windows">Windows Authentication</SelectItem>
                    </SelectContent>
                  </Select>

                  {settings.sql_auth_type === 'sql' && (
                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label>Username</Label>
                        <Input
                          placeholder="sql_user"
                          value={settings.sql_username || ''}
                          onChange={(e) => updateSetting('sql_username', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Password</Label>
                        <Input
                          type="password"
                          placeholder="••••••••"
                          value={settings.sql_password || ''}
                          onChange={(e) => updateSetting('sql_password', e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t pt-6 flex items-center gap-4">
                <Button variant="outline" onClick={handleTestConnection} disabled={testing || !settings.sql_server}>
                  {testing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <TestTube className="w-4 h-4 mr-2" />}
                  Test Connection
                </Button>
                {testResult && (
                  <span className={`text-sm ${testResult.success ? 'text-green-600' : 'text-red-600'}`}>
                    {testResult.message}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stored Procedures Tab */}
        <TabsContent value="procedures">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileCode className="w-5 h-5" />
                Stored Procedures
              </CardTitle>
              <CardDescription>
                Configure the stored procedures for data retrieval
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Timesheet Stored Procedure</Label>
                <Input
                  placeholder="e.g., dbo.usp_GetWeeklyTimesheets"
                  value={settings.timesheet_stored_proc || ''}
                  onChange={(e) => updateSetting('timesheet_stored_proc', e.target.value)}
                />
                <p className="text-xs text-slate-500">
                  Must accept parameter: <code className="bg-slate-100 px-1 rounded">@WeekCommencing DATE</code>
                </p>
              </div>

              <div className="space-y-2">
                <Label>Docket Stored Procedure</Label>
                <Input
                  placeholder="e.g., dbo.usp_GetWeeklyDockets"
                  value={settings.docket_stored_proc || ''}
                  onChange={(e) => updateSetting('docket_stored_proc', e.target.value)}
                />
                <p className="text-xs text-slate-500">
                  Must accept parameter: <code className="bg-slate-100 px-1 rounded">@WeekCommencing DATE</code>
                </p>
              </div>

              <div className="space-y-2">
                <Label>Docket Items Stored Procedure</Label>
                <Input
                  placeholder="e.g., dbo.usp_GetWeeklyDocketItems"
                  value={settings.docket_items_stored_proc || ''}
                  onChange={(e) => updateSetting('docket_items_stored_proc', e.target.value)}
                />
                <p className="text-xs text-slate-500">
                  Must accept parameter: <code className="bg-slate-100 px-1 rounded">@WeekCommencing DATE</code>. 
                  Returns line items linked by <code className="bg-slate-100 px-1 rounded">DocketNum</code>
                </p>
              </div>

              {/* Manual Fetch Section */}
              <div className="border-t pt-6 mt-6">
                <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                  <Play className="w-4 h-4" />
                  Manual Data Fetch
                </h3>
                <div className="bg-slate-50 rounded-lg p-4 space-y-4">
                  <div className="space-y-2">
                    <Label>Week Commencing Date</Label>
                    <Input
                      type="date"
                      value={weekCommencing}
                      onChange={(e) => setWeekCommencing(e.target.value)}
                      className="max-w-[200px]"
                    />
                    <p className="text-xs text-slate-500">
                      Select the Monday of the week to fetch
                    </p>
                  </div>
                  <Button 
                    onClick={handleFetchWeekData}
                    disabled={fetchingWeekData || !weekCommencing || !settings.timesheet_stored_proc}
                  >
                    {fetchingWeekData ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Fetching Data...
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4 mr-2" />
                        Fetch Week Data
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-slate-500">
                    Data will be fetched and displayed for preview before import
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 text-sm">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium text-slate-700 mb-2">Timesheet Columns</p>
                  <ul className="space-y-1 text-slate-600 text-xs">
                    <li><code className="bg-slate-200 px-1 rounded">EmployeeID</code></li>
                    <li><code className="bg-slate-200 px-1 rounded">Employee</code></li>
                    <li><code className="bg-slate-200 px-1 rounded">Date</code></li>
                    <li><code className="bg-slate-200 px-1 rounded">StartTime</code></li>
                    <li><code className="bg-slate-200 px-1 rounded">FinishTime</code></li>
                    <li><code className="bg-slate-200 px-1 rounded">MealHours</code></li>
                    <li><code className="bg-slate-200 px-1 rounded">Notes</code></li>
                  </ul>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium text-slate-700 mb-2">Docket Columns</p>
                  <ul className="space-y-1 text-slate-600 text-xs">
                    <li><code className="bg-slate-200 px-1 rounded">EmployeeID</code></li>
                    <li><code className="bg-slate-200 px-1 rounded">JobDate</code></li>
                    <li><code className="bg-slate-200 px-1 rounded">StartTime</code> / <code className="bg-slate-200 px-1 rounded">FinishTime</code></li>
                    <li><code className="bg-slate-200 px-1 rounded">InternalJobNumber</code></li>
                    <li><code className="bg-slate-200 px-1 rounded">WorkerRole</code></li>
                    <li><code className="bg-slate-200 px-1 rounded">MachineName</code></li>
                    <li><code className="bg-slate-200 px-1 rounded">DocketNum</code></li>
                    <li><code className="bg-slate-200 px-1 rounded">Customer</code></li>
                  </ul>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium text-slate-700 mb-2">Docket Item Columns</p>
                  <ul className="space-y-1 text-slate-600 text-xs">
                    <li><code className="bg-slate-200 px-1 rounded">DocketNum</code> (key)</li>
                    <li><code className="bg-slate-200 px-1 rounded">Description</code></li>
                    <li><code className="bg-slate-200 px-1 rounded">Unit</code></li>
                    <li><code className="bg-slate-200 px-1 rounded">AccountID</code></li>
                    <li><code className="bg-slate-200 px-1 rounded">Price</code></li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sync Schedule Tab */}
        <TabsContent value="schedule">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Automatic Sync Schedule
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Clock className={`w-5 h-5 ${settings.sync_enabled ? 'text-green-600' : 'text-slate-400'}`} />
                    <div>
                      <p className="font-medium">Automatic Sync</p>
                      <p className="text-sm text-slate-500">
                        {settings.sync_enabled 
                          ? `Every ${settings.sync_day} at ${settings.sync_time} (${settings.sync_timezone})`
                          : 'Disabled'
                        }
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.sync_enabled}
                    onCheckedChange={(checked) => updateSetting('sync_enabled', checked)}
                  />
                </div>

                {settings.sync_enabled && (
                  <div className="grid grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label>Day of Week</Label>
                      <Select value={settings.sync_day} onValueChange={(v) => updateSetting('sync_day', v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {DAYS_OF_WEEK.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Time</Label>
                      <Input type="time" value={settings.sync_time || '22:00'} onChange={(e) => updateSetting('sync_time', e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Timezone</Label>
                      <Select value={settings.sync_timezone} onValueChange={(v) => updateSetting('sync_timezone', v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {TIMEZONES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
                
                {/* Scheduler Status */}
                {schedulerStatus && schedulerStatus.job_scheduled && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-800">Scheduled Sync Active</span>
                    </div>
                    <p className="text-sm text-green-700">
                      <strong>Next sync:</strong> {schedulerStatus.next_run_time_formatted}
                    </p>
                  </div>
                )}
                
                {schedulerStatus && !schedulerStatus.job_scheduled && settings.sync_enabled && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="w-5 h-5 text-amber-600" />
                      <span className="font-medium text-amber-800">Save Required</span>
                    </div>
                    <p className="text-sm text-amber-700">
                      Click &quot;Save Settings&quot; to activate the schedule
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Manual Sync</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button onClick={handleSyncNow} disabled={syncing || !settings.sql_server}>
                  {syncing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                  Sync Now
                </Button>
                {settings.last_sync_at && (
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Last Sync</span>
                      {getSyncStatusBadge()}
                    </div>
                    <p className="text-sm text-slate-500">{new Date(settings.last_sync_at).toLocaleString()}</p>
                    {settings.last_sync_message && <p className="text-sm mt-1">{settings.last_sync_message}</p>}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Role Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingRole ? 'Edit Role' : 'Add Role'}</DialogTitle>
            <DialogDescription>
              {editingRole?.is_system ? 'System roles can only have their description edited.' : 'Create a custom role for matching docket data.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Role Name</Label>
              <Input
                placeholder="e.g., Senior Operator"
                value={roleForm.name}
                onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                disabled={editingRole?.is_system}
              />
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea
                placeholder="Brief description of this role..."
                value={roleForm.description}
                onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveRole}>{editingRole ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Equipment Dialog */}
      <Dialog open={equipmentDialogOpen} onOpenChange={setEquipmentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingEquipment ? 'Edit Equipment Type' : 'Add Equipment Type'}</DialogTitle>
            <DialogDescription>
              Define equipment types for matching docket data and rules.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Equipment Name</Label>
              <Input
                placeholder="e.g., 50T Mobile Crane"
                value={equipmentForm.name}
                onChange={(e) => setEquipmentForm({ ...equipmentForm, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={equipmentForm.category} onValueChange={(v) => setEquipmentForm({ ...equipmentForm, category: v })}>
                <SelectTrigger><SelectValue placeholder="Select category..." /></SelectTrigger>
                <SelectContent>
                  {EQUIPMENT_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea
                placeholder="Brief description..."
                value={equipmentForm.description}
                onChange={(e) => setEquipmentForm({ ...equipmentForm, description: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEquipmentDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEquipment}>{editingEquipment ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Holiday Dialog */}
      <Dialog open={holidayDialogOpen} onOpenChange={setHolidayDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingHoliday ? 'Edit Public Holiday' : 'Add Public Holiday'}</DialogTitle>
            <DialogDescription>
              Define public holidays that trigger overtime rates for all hours worked.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={holidayForm.date}
                onChange={(e) => setHolidayForm({ ...holidayForm, date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Holiday Name</Label>
              <Input
                placeholder="e.g., Christmas Day"
                value={holidayForm.name}
                onChange={(e) => setHolidayForm({ ...holidayForm, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>State (optional)</Label>
              <Select value={holidayForm.state || 'all'} onValueChange={(v) => setHolidayForm({ ...holidayForm, state: v === 'all' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="All states..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All States</SelectItem>
                  <SelectItem value="NSW">New South Wales</SelectItem>
                  <SelectItem value="VIC">Victoria</SelectItem>
                  <SelectItem value="QLD">Queensland</SelectItem>
                  <SelectItem value="WA">Western Australia</SelectItem>
                  <SelectItem value="SA">South Australia</SelectItem>
                  <SelectItem value="TAS">Tasmania</SelectItem>
                  <SelectItem value="NT">Northern Territory</SelectItem>
                  <SelectItem value="ACT">Australian Capital Territory</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">Leave blank for national holidays</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHolidayDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveHoliday}>{editingHoliday ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Employee Type Dialog */}
      <Dialog open={employeeTypeDialogOpen} onOpenChange={setEmployeeTypeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingEmployeeType ? 'Edit Employee Type' : 'Add Employee Type'}</DialogTitle>
            <DialogDescription>
              Define employee classifications to control which rules apply during payroll processing.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                placeholder="e.g., Trainee"
                value={employeeTypeForm.name}
                onChange={(e) => setEmployeeTypeForm({ ...employeeTypeForm, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                placeholder="e.g., Training employees - limited allowances"
                value={employeeTypeForm.description}
                onChange={(e) => setEmployeeTypeForm({ ...employeeTypeForm, description: e.target.value })}
              />
            </div>
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <Label className="text-base">Receives All Allowances</Label>
                <p className="text-xs text-slate-500 mt-1">
                  When enabled, employees of this type receive all applicable allowances (equipment, site, travel, etc.).<br />
                  When disabled, they only receive base allowances (meal, crib).
                </p>
              </div>
              <Switch
                checked={employeeTypeForm.receives_all_allowances}
                onCheckedChange={(v) => setEmployeeTypeForm({ ...employeeTypeForm, receives_all_allowances: v })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmployeeTypeDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEmployeeType}>{editingEmployeeType ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Week Data Preview Modal */}
      <Dialog open={previewModalOpen} onOpenChange={(open) => !open && handleCancelPreview()}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-600" />
              Preview Import Data
            </DialogTitle>
            <DialogDescription>
              {previewData && (
                <span>
                  <strong>{previewData.batch_name}</strong> — Review the data below before importing
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {previewData && (
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-3 mb-4">
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-700">{previewData.summary?.timesheet_count || 0}</p>
                  <p className="text-xs text-blue-600">Timesheets</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-amber-700">{previewData.summary?.docket_count || 0}</p>
                  <p className="text-xs text-amber-600">Dockets</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-700">{previewData.summary?.employee_count || 0}</p>
                  <p className="text-xs text-green-600">Employees</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-sm font-medium text-slate-700">
                    {previewData.summary?.date_range?.start || '—'}
                  </p>
                  <p className="text-xs text-slate-500">to {previewData.summary?.date_range?.end || '—'}</p>
                </div>
              </div>
              
              {/* Tabs for Timesheet and Docket Data */}
              <Tabs defaultValue="timesheets" className="flex-1 flex flex-col overflow-hidden">
                <TabsList className="mb-2">
                  <TabsTrigger value="timesheets" className="gap-2">
                    <FileSpreadsheet className="w-4 h-4" />
                    Timesheet Data ({previewData.timesheet_entries?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger value="dockets" className="gap-2">
                    <ClipboardList className="w-4 h-4" />
                    Docket Data ({previewData.docket_entries?.length || 0})
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="timesheets" className="flex-1 overflow-auto border rounded-lg">
                  {previewData.timesheet_entries?.length > 0 ? (
                    <Table>
                      <TableHeader className="sticky top-0 bg-white z-10">
                        <TableRow>
                          <TableHead className="min-w-[80px]">Emp ID</TableHead>
                          <TableHead className="min-w-[120px]">Employee</TableHead>
                          <TableHead className="min-w-[90px]">Date</TableHead>
                          <TableHead>Start</TableHead>
                          <TableHead>Finish</TableHead>
                          <TableHead>Break</TableHead>
                          <TableHead className="min-w-[200px]">Notes</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {previewData.timesheet_entries.map((entry, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-mono text-xs">{entry.employee_id}</TableCell>
                            <TableCell className="text-sm">{entry.employee_name}</TableCell>
                            <TableCell className="text-xs">{entry.date}</TableCell>
                            <TableCell className="text-xs">{entry.start_time}</TableCell>
                            <TableCell className="text-xs">{entry.end_time}</TableCell>
                            <TableCell className="text-xs">{entry.break_minutes || 0}m</TableCell>
                            <TableCell className="text-xs text-slate-600 max-w-[250px]" title={entry.timesheet_notes}>
                              {entry.timesheet_notes || '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="flex items-center justify-center h-40 text-slate-500">
                      <div className="text-center">
                        <FileSpreadsheet className="w-10 h-10 mx-auto mb-2 opacity-30" />
                        <p>No timesheet entries found</p>
                      </div>
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="dockets" className="flex-1 overflow-auto border rounded-lg">
                  {previewData.docket_entries?.length > 0 ? (
                    <Table>
                      <TableHeader className="sticky top-0 bg-white z-10">
                        <TableRow>
                          <TableHead>Docket #</TableHead>
                          <TableHead className="min-w-[80px]">Emp ID</TableHead>
                          <TableHead className="min-w-[120px]">Employee</TableHead>
                          <TableHead className="min-w-[90px]">Date</TableHead>
                          <TableHead>Start</TableHead>
                          <TableHead>End</TableHead>
                          <TableHead>Break</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Equipment</TableHead>
                          <TableHead>Job #</TableHead>
                          <TableHead>Customer</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {previewData.docket_entries.map((entry, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-mono text-xs">{entry.docket_number || '—'}</TableCell>
                            <TableCell className="font-mono text-xs">{entry.employee_id}</TableCell>
                            <TableCell className="text-sm">{entry.employee_name}</TableCell>
                            <TableCell className="text-xs">{entry.date}</TableCell>
                            <TableCell className="text-xs">{entry.start_time}</TableCell>
                            <TableCell className="text-xs">{entry.end_time}</TableCell>
                            <TableCell className="text-xs">{entry.break_minutes || 0}m</TableCell>
                            <TableCell>
                              {entry.role ? <Badge variant="outline" className="text-xs">{entry.role}</Badge> : '—'}
                            </TableCell>
                            <TableCell className="text-xs max-w-[100px] truncate" title={entry.equipment_type}>{entry.equipment_type || '—'}</TableCell>
                            <TableCell className="font-mono text-xs">{entry.site_id || '—'}</TableCell>
                            <TableCell className="text-xs max-w-[150px] truncate" title={entry.site_name}>{entry.site_name || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="flex items-center justify-center h-40 text-slate-500">
                      <div className="text-center">
                        <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-30" />
                        <p>No docket entries found</p>
                      </div>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          )}
          
          <DialogFooter className="mt-4 pt-4 border-t">
            <Button variant="outline" onClick={handleCancelPreview} disabled={confirmingImport}>
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmImport} 
              disabled={confirmingImport || (!previewData?.timesheet_entries?.length && !previewData?.docket_entries?.length)}
              className="gap-2"
            >
              {confirmingImport ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Confirm Import
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Success Modal */}
      <Dialog open={importSuccessModalOpen} onOpenChange={handleCloseSuccessModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
            </div>
            <DialogTitle className="text-center text-xl">Import Successful!</DialogTitle>
            <DialogDescription className="text-center">
              {importSuccessData?.batch_name}
            </DialogDescription>
          </DialogHeader>
          
          {importSuccessData && (
            <div className="space-y-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-700">{importSuccessData.timesheet_count}</p>
                  <p className="text-xs text-blue-600">Timesheets</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-amber-700">{importSuccessData.docket_count}</p>
                  <p className="text-xs text-amber-600">Dockets</p>
                </div>
              </div>
              
              {/* Validation Summary */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-medium text-slate-700 mb-2">Validation Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Total Validation Records:</span>
                    <span className="font-medium">{importSuccessData.validation_count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-green-600">Matched:</span>
                    <span className="font-medium text-green-700">{importSuccessData.matched_count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-amber-600">Discrepancies:</span>
                    <span className="font-medium text-amber-700">{importSuccessData.discrepancy_count}</span>
                  </div>
                </div>
              </div>
              
              {/* Next Steps */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Next Step
                </h4>
                <p className="text-sm text-blue-700 mt-1">
                  Go to the <strong>Timesheets</strong> page to review and verify the imported data, 
                  resolve any discrepancies, and approve entries for payroll processing.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter className="mt-4 flex gap-2">
            <Button variant="outline" onClick={handleCloseSuccessModal}>
              Close
            </Button>
            <Button onClick={() => { handleCloseSuccessModal(); navigate('/timesheets'); }} className="gap-2">
              Go to Timesheets
              <ArrowRight className="w-4 h-4" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;
